/**
 * SnippetCard — shared install-snippet display card.
 *
 * Two modes:
 *   - "static-badge": shows a static green check + badge text on the right.
 *     Used on marketing surfaces ("Copy-paste once" badge).
 *   - "copy-button": shows a clickable Copy button that copies the snippet
 *     to clipboard. Used in onboarding Step 2.
 *
 * Same visual shell either way — white card, slate border, top bar with
 * uppercase label left + badge/button right, monospace code below in a
 * tinted code well.
 *
 * Translated from heymotek-web's Astro SnippetCard component.
 * See STATUS/PLAN_ONBOARDING_REDESIGN_PORT.md.
 */

import { useState } from 'react';
import { Check, Copy } from 'lucide-react';

interface BaseProps {
  /** Top-left uppercase label (e.g., "THE SNIPPET" or "INSTALL CODE"). */
  label?: string;
  /** The literal code to display. */
  snippet: string;
}

interface StaticBadgeProps extends BaseProps {
  mode: 'static-badge';
  /** Text inside the static badge. */
  badgeText?: string;
}

interface CopyButtonProps extends BaseProps {
  mode: 'copy-button';
}

type Props = StaticBadgeProps | CopyButtonProps;

export function SnippetCard(props: Props) {
  const { label = 'The snippet', snippet } = props;
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — silently no-op
    }
  }

  return (
    <div className="bg-card rounded-2xl shadow-card border border-dark/5 p-6">
      <div className="flex items-center justify-between mb-4 gap-3">
        <span className="text-xs font-bold text-dark uppercase tracking-wider">
          {label}
        </span>

        {props.mode === 'static-badge' && (
          <span className="text-xs text-green-700 font-semibold inline-flex items-center gap-1.5 shrink-0">
            <Check className="w-3 h-3" strokeWidth={2.5} />
            {props.badgeText ?? 'Copy-paste once'}
          </span>
        )}

        {props.mode === 'copy-button' && (
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-info text-white px-3 py-1.5 text-xs font-bold hover:bg-info/90 transition-colors"
          >
            <Copy className="h-3.5 w-3.5" />
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        )}
      </div>

      <pre className="text-sm font-mono text-dark overflow-x-auto leading-relaxed bg-bg-tint border border-dark/10 rounded-lg p-4">
        <code>{snippet}</code>
      </pre>
    </div>
  );
}
